import * as Sentry from '@sentry/react';
import { Flex, Tooltip } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from 'react';

import { useTandaClockin } from '../../hook/useTandaClockin';
import {
  TandaClockinDto,
  TandaClockinSnapshot,
  TandaClockinStatus,
} from './types';

export type {
  ClockType,
  TandaClockinDto,
  TandaClockinLatestBatchResponse,
  TandaClockinSnapshot,
  TandaClockinStatus,
} from './types';

dayjs.extend(utc);

const COLOR_ONLINE = '#22C55E';
const COLOR_OFFLINE = '#DC2626';

/** Public external-store surface (what consumers see via context). The
 *  internal mutator used by the provider effect is on `InternalStore`
 *  below — exported via the ref only. */
interface TandaClockinStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => TandaClockinSnapshot;
  getUserEntry: (userId: string) => TandaClockinDto | undefined;
}

/** Store with the provider-only `_publish` mutator. Keeps the internal
 *  state object and the listener Set fully encapsulated so callers outside
 *  this file don't need TypeScript escape hatches to read or write them. */
type InternalStore = TandaClockinStore & {
  _publish: (snapshot: TandaClockinSnapshot) => void;
};

interface TandaClockinProviderProps {
  companyId?: string;
  employeeIds: string[];
  children: ReactNode;
}

/**
 * Provider mounted once at the kanban root. Calls `useTandaClockin` to fetch
 * the snapshot and subscribe to STOMP, then publishes the resulting data to
 * descendants via an external store with `useSyncExternalStore`-based
 * subscriptions.
 *
 * Why an external store instead of plain Context?
 * - Every STOMP frame produces a new `Map` reference. With plain Context,
 *   ALL consumers (every badge + every column) re-render on every frame.
 *   At shift change that's dozens of frames/sec × O(N×M) re-renders.
 * - With `useSyncExternalStore`, each `ClockStatusBadge` subscribes to its
 *   own `userId` slot. The store returns the same DTO reference when that
 *   user's data hasn't changed, so React skips the re-render. Only badges
 *   for employees whose entry actually changed re-render.
 */
export const TandaClockinProvider: FC<TandaClockinProviderProps> = ({
  companyId,
  employeeIds,
  children,
}) => {
  const { latestByEmployee, snapshotLoaded, snapshotDegraded } =
    useTandaClockin(companyId, employeeIds);

  // Store is created lazily once per provider instance. State + listeners
  // live on the instance so they can be mutated from `useEffect` and read
  // by listeners without round-tripping through React state.
  const storeRef = useRef<InternalStore>();
  if (!storeRef.current) {
    const listeners = new Set<() => void>();
    const state: { current: TandaClockinSnapshot } = {
      current: {
        latestByEmployee: new Map(),
        snapshotLoaded: false,
        snapshotDegraded: false,
      },
    };
    storeRef.current = {
      subscribe: (listener) => {
        listeners.add(listener);
        return () => {
          listeners.delete(listener);
        };
      },
      getSnapshot: () => state.current,
      getUserEntry: (userId) => state.current.latestByEmployee.get(userId),
      _publish: (snapshot) => {
        state.current = snapshot;
        // Isolate each listener so one consumer's render crash doesn't
        // skip notifications for the rest of the subscribers. Capture to
        // Sentry (gated by env in @sentry/react) so dev sees it in the
        // console and prod gets the full breadcrumb trail.
        listeners.forEach((l) => {
          try {
            l();
          } catch (e) {
            console.error('[tanda-clockin-store] listener threw:', e);
            Sentry.captureException(e, {
              tags: { source: 'TandaClockinStore._publish' },
            });
          }
        });
      },
    };
  }
  const store = storeRef.current;

  // Push the latest snapshot into the store and notify listeners.
  useEffect(() => {
    store._publish({
      latestByEmployee,
      snapshotLoaded,
      snapshotDegraded,
    });
  }, [latestByEmployee, snapshotLoaded, snapshotDegraded, store]);

  return (
    <TandaClockinStoreContext.Provider value={store}>
      {children}
    </TandaClockinStoreContext.Provider>
  );
};

const TandaClockinStoreContext = createContext<TandaClockinStore | null>(null);

/** Per-userId accessor for badges. Only re-renders when this userId's entry
 *  is added, removed, or replaced — NOT on every STOMP frame for other users.
 *  Returns the entry plus the status flags (so consumers can show a degraded
 *  warning without subscribing to the full snapshot). */
export const useTandaClockinForUser = (
  userId: string
): { entry: TandaClockinDto | undefined } & TandaClockinStatus => {
  const store = useContext(TandaClockinStoreContext);
  if (!store) {
    throw new Error(
      'useTandaClockinForUser must be used within a TandaClockinProvider'
    );
  }
  // Stable selectors so `useSyncExternalStore` can short-circuit on
  // identical snapshot reads (React tolerates fresh arrows but stable
  // references keep the contract explicit).
  const getEntry = useCallback(
    () => store.getUserEntry(userId),
    [store, userId]
  );
  const getSnapshotLoaded = useCallback(
    () => store.getSnapshot().snapshotLoaded,
    [store]
  );
  const getSnapshotDegraded = useCallback(
    () => store.getSnapshot().snapshotDegraded,
    [store]
  );
  // Three separate subscriptions so each value is independently stable:
  // - `entry` only changes when THIS user's data changes.
  // - `snapshotLoaded` only changes on the false→true transition.
  // - `snapshotDegraded` only changes on false↔true transitions.
  const entry = useSyncExternalStore(store.subscribe, getEntry);
  const snapshotLoaded = useSyncExternalStore(
    store.subscribe,
    getSnapshotLoaded
  );
  const snapshotDegraded = useSyncExternalStore(
    store.subscribe,
    getSnapshotDegraded
  );
  return { entry, snapshotLoaded, snapshotDegraded };
};

/** Full snapshot accessor for components that need the whole map
 *  (e.g. column warning counts). Re-renders on every snapshot/STOMP
 *  update; pair with `useMemo` keyed on the map to avoid recomputation. */
export const useTandaClockinSnapshot = (): TandaClockinSnapshot => {
  const store = useContext(TandaClockinStoreContext);
  if (!store) {
    throw new Error(
      'useTandaClockinSnapshot must be used within a TandaClockinProvider'
    );
  }
  return useSyncExternalStore(store.subscribe, store.getSnapshot);
};

/** Legacy `{ latestByEmployee, snapshotLoaded, snapshotDegraded }` accessor.
 *  Field names now match `TandaClockinSnapshot` exactly, so this is just a
 *  re-export of the snapshot — kept under its old name so existing call
 *  sites don't need to change. */
export const useTandaClockinMap = useTandaClockinSnapshot;

interface ClockStatusBadgeProps {
  /** Employee's `userId` from `AssignEmployeeItem`. */
  userId: string;
}

/**
 * Renders the clock-in / clock-out indicator for one employee based on
 * today's per-(user,date) rollup.
 *
 * Render rules:
 * - `start`           → online  (green up-circle  + "Clocked in HH:mm")
 * - `finish`          → offline (red   down-circle + "Clocked out HH:mm")
 * - no rollup today   → nothing rendered
 *
 * Uses `useTandaClockinForUser` so the badge only re-renders when its
 * specific employee's entry changes, not on every other employee's
 * STOMP frame.
 */
export const ClockStatusBadge: FC<ClockStatusBadgeProps> = ({ userId }) => {
  const { entry: latest, snapshotLoaded } = useTandaClockinForUser(userId);

  if (!snapshotLoaded || !latest) {
    return null;
  }

  const isOnline = latest.type === 'start';
  const color = isOnline ? COLOR_ONLINE : COLOR_OFFLINE;
  const hasTime = latest.clockin_at !== null;
  // `clockin_at` is an ISO-8601 timestamp with explicit `+00:00` offset.
  // Parse as UTC, then convert to the user's local timezone so the badge
  // shows the local clock time at the moment the user clocked in/out.
  const timeStr = hasTime
    ? dayjs.utc(latest.clockin_at).local().format('HH:mm')
    : '';
  const tooltipText = hasTime
    ? `${isOnline ? 'Clocked in' : 'Clocked out'} at ${timeStr}`
    : `${isOnline ? 'Clocked in' : 'Clocked out'}`;

  // `stopPropagation` guards keep the parent's dnd-kit listeners from
  // treating a click on the badge as the start of a card drag.
  return (
    <Flex
      justify="flex-end"
      align="center"
      className="w-full pt-1"
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <Tooltip title={tooltipText}>
        <Flex
          align="center"
          gap={6}
          className="text-xs font-medium cursor-default"
          style={{ color }}
        >
          <span>({timeStr})</span>
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              borderRadius: 6,
              backgroundColor: color,
            }}
          />
        </Flex>
      </Tooltip>
    </Flex>
  );
};
